/**
 * Recently viewed products.
 *
 * Storage: localStorage key `pv-recently-viewed`
 *   → JSON array of product handles, most-recent first, deduped, capped at MAX.
 *
 * Behavior:
 *   • On any PDP (the section reads `product.handle` and writes it via
 *     the `data-current-handle` attribute), prepend the current handle,
 *     dedupe, and persist.
 *   • On any page containing a <recently-viewed> section, hydrate cards
 *     for the stored handles (excluding the current one) up to `data-limit`.
 *
 * Hydration uses Shopify's `/products/{handle}.js` endpoint (JSON, public).
 */

const STORAGE_KEY = 'pv-recently-viewed';
const MAX = 12;

function readList() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((h) => typeof h === 'string') : [];
  } catch {
    return [];
  }
}

function writeList(handles) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(handles.slice(0, MAX)));
  } catch {
    /* quota / privacy mode — silently drop */
  }
}

function recordVisit(handle) {
  if (!handle) return;
  const list = readList();
  const next = [handle, ...list.filter((h) => h !== handle)];
  writeList(next);
}

function formatMoney(cents) {
  // Rely on Shopify's per-shop money format if available, else fall back.
  if (window.Shopify && window.Shopify.formatMoney) {
    return window.Shopify.formatMoney(cents, window.Shopify.money_format);
  }
  return `$${(cents / 100).toFixed(2)}`;
}

async function fetchProduct(handle) {
  try {
    const res = await fetch(`/products/${encodeURIComponent(handle)}.js`, {
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

class RecentlyViewed extends HTMLElement {
  connectedCallback() {
    const currentHandle = this.dataset.currentHandle || '';
    if (currentHandle) recordVisit(currentHandle);

    const limit = parseInt(this.dataset.limit || '4', 10);
    const list = this.querySelector('[data-list]');
    const template = this.querySelector('template[data-recently-viewed-card]');
    if (!list || !template) return;

    const handles = readList()
      .filter((h) => h !== currentHandle)
      .slice(0, limit);

    if (handles.length === 0) {
      // Nothing to show — hide the whole section so we don't render an empty block.
      this.hidden = true;
      return;
    }

    Promise.all(handles.map(fetchProduct)).then((products) => {
      const usable = products.filter(Boolean);
      if (usable.length === 0) {
        this.hidden = true;
        return;
      }

      const frag = document.createDocumentFragment();
      for (const p of usable) {
        const node = template.content.firstElementChild.cloneNode(true);
        const link = node.querySelector('[data-link]');
        const image = node.querySelector('[data-image]');
        const title = node.querySelector('[data-title]');
        const price = node.querySelector('[data-price]');

        link.href = p.url;
        title.textContent = p.title;
        price.textContent = formatMoney(p.price);
        if (p.featured_image) {
          image.src = p.featured_image;
          image.alt = p.title;
        } else {
          image.remove();
        }
        frag.appendChild(node);
      }
      list.appendChild(frag);
      list.hidden = false;
    });
  }
}

customElements.define('recently-viewed', RecentlyViewed);
